if (!customElements.get("free-gift-auto-add")) {
  class FreeGiftAutoAdd extends HTMLElement {
    constructor() {
      super();

      this.enabled = this.dataset.enabled === "true";
      this.giftVariantId = this.dataset.giftVariantId;
      this.threshold = Number(this.dataset.threshold) * 100; // Convert to cents
      this.isProcessing = false;
    }

    connectedCallback() {
      console.log('[Free Gift] Component initialized', {
        enabled: this.enabled,
        giftVariantId: this.giftVariantId,
        threshold: this.threshold / 100
      });

      if (!this.enabled || !this.giftVariantId) {
        console.warn('[Free Gift] Component disabled or no variant ID');
        return;
      }

      // Subscribe to cart updates
      this.cartUpdateUnsubscriber = window.FoxThemeEvents.subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        (state) => {
          console.log('[Free Gift] Cart update event received');
          this.handleCartUpdate(state);
        }
      );

      // Subscribe to cart drawer open event
      this.cartDrawerOpenUnsubscriber = window.FoxThemeEvents.subscribe(
        'ON_CART_DRAWER_OPEN',
        (state) => {
          console.log('[Free Gift] Cart drawer open event received');
          this.handleCartUpdate(state);
        }
      );

      // Check cart state immediately on load
      this.checkInitialCartState();
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
      if (this.cartDrawerOpenUnsubscriber) {
        this.cartDrawerOpenUnsubscriber();
      }
    }

    async checkInitialCartState() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) {
          console.warn('[Free Gift] Failed to fetch cart');
          return;
        }
        const cart = await response.json();
        await this.handleCartUpdate(cart);
      } catch (error) {
        console.error('[Free Gift] Error checking initial cart state:', error);
      }
    }

    async handleCartUpdate(state) {
      if (this.isProcessing) return;

      // If state is not a full cart object (e.g., just an added item), fetch the full cart
      if (!state || typeof state.items_subtotal_price === 'undefined' || !Array.isArray(state.items)) {
        console.log('[Free Gift] Received item response, fetching full cart state...');
        try {
          const response = await fetch('/cart.js');
          if (!response.ok) {
            console.error('[Free Gift] Failed to fetch cart');
            return;
          }
          const cart = await response.json();
          state = cart;
        } catch (error) {
          console.error('[Free Gift] Error fetching cart:', error);
          return;
        }
      }

      const cartTotal = state.items_subtotal_price;
      const hasGift = this.checkIfGiftInCart(state.items);
      const shouldHaveGift = cartTotal >= this.threshold;

      console.log('[Free Gift] Cart check:', {
        cartTotal: cartTotal / 100,
        threshold: this.threshold / 100,
        hasGift,
        shouldHaveGift,
        giftVariantId: this.giftVariantId
      });

      // Auto-add gift if threshold reached and not in cart
      if (shouldHaveGift && !hasGift) {
        console.log('[Free Gift] Adding gift to cart...');
        await this.addGiftToCart();
      }
      // Auto-remove gift if below threshold
      else if (!shouldHaveGift && hasGift) {
        console.log('[Free Gift] Removing gift from cart...');
        await this.removeGiftFromCart();
      }
    }

    checkIfGiftInCart(items) {
      if (!Array.isArray(items)) return false;
      return items.some(item => item.variant_id == this.giftVariantId);
    }

    async addGiftToCart() {
      this.isProcessing = true;

      try {
        const body = {
          updates: {
            [this.giftVariantId]: 1
          },
          sections: this.getSections(),
          sections_url: window.location.pathname
        };

        const response = await fetch(window.FoxThemeSettings.routes.cart_update_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify(body)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const cart = await response.json();

        // Emit cart update event
        window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, cart);

        // Update cart drawer if it exists
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer && cart.sections) {
          cartDrawer.renderContents(cart);
        }

      } catch (error) {
        console.error("Free gift add error:", error);
      } finally {
        this.isProcessing = false;
      }
    }

    async removeGiftFromCart() {
      this.isProcessing = true;

      try {
        const body = {
          updates: {
            [this.giftVariantId]: 0
          },
          sections: this.getSections(),
          sections_url: window.location.pathname
        };

        const response = await fetch(window.FoxThemeSettings.routes.cart_update_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify(body)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const cart = await response.json();

        // Emit cart update event
        window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, cart);

        // Update cart drawer if it exists
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer && cart.sections) {
          cartDrawer.renderContents(cart);
        }

      } catch (error) {
        console.error("Free gift remove error:", error);
      } finally {
        this.isProcessing = false;
      }
    }

    getSections() {
      const cartDrawer = document.querySelector("cart-drawer");
      const mainCart = document.querySelector("cart-items");

      if (cartDrawer) {
        return cartDrawer.getSectionsToRender().map(s => s.id);
      } else if (mainCart) {
        return mainCart.getSectionsToRender().map(s => s.section);
      }
      return [];
    }
  }

  customElements.define("free-gift-auto-add", FreeGiftAutoAdd);
}
